import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AlertService, Alert } from './service/alert.service';
import { faIcons } from '../../shared/icons/fa-icons';
import { CusReviewService } from '../../features/customer/review/service/cus-review.service';


@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  styles: [`
    .alert-overlay {
      position: fixed;
      top: 20px;
      right: 20px;
      left: auto;
      z-index: 9999 !important;
      min-width: 380px;
      max-width: 460px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    /* ── Toast card ─────────────────────────────────── */
    .toast-card {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: 20px;
      padding: 16px 40px 16px 18px;
      border-radius: 18px;
      background: rgba(15, 21, 35, 0.62);
      backdrop-filter: blur(16px) saturate(140%);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.18);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
      color: #e5e7eb;
      overflow: hidden;
      animation: slideIn 0.3s ease-out;
    }
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to   { transform: translateX(0); opacity: 1; }
    }

    /* left accent glow bar, colored per type */
    .toast-card::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 18px;
      padding: 1px;
      pointer-events: none;
    }

    .toast-card::after {
      content: '';
      position: absolute;
      left: 0;
      top: 8%;
      bottom: 8%;
      width: 4px;
      border-radius: 4px;
    }

    .toast-success::after { background: #22c55e; box-shadow: 0 0 12px 1px rgba(34,197,94,.7); }
    .toast-error::after   { background: #ef4444; box-shadow: 0 0 12px 1px rgba(239,68,68,.7); }
    .toast-warning::after { background: #f59e0b; box-shadow: 0 0 12px 1px rgba(245,158,11,.7); }
    .toast-info::after    { background: #3b82f6; box-shadow: 0 0 12px 1px rgba(59,130,246,.7); }

    .toast-success { box-shadow: 0 12px 32px rgba(0,0,0,.45), 0 0 28px -8px rgba(34,197,94,.4); }
    .toast-error   { box-shadow: 0 12px 32px rgba(0,0,0,.45), 0 0 28px -8px rgba(239,68,68,.4); }
    .toast-warning { box-shadow: 0 12px 32px rgba(0,0,0,.45), 0 0 28px -8px rgba(245,158,11,.4); }
    .toast-info    { box-shadow: 0 12px 32px rgba(0,0,0,.45), 0 0 28px -8px rgba(59,130,246,.4); }

    /* ── Icon badge ─────────────────────────────────── */
    .toast-icon-badge {
      flex-shrink: 0;
      width: 35px;
      height: 35px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.05rem;
      color: #fff;
      transition: width 0.15s ease, height 0.15s ease, font-size 0.15s ease;
    }

    .toast-icon-badge.success {
      background: radial-gradient(circle at 35% 30%, #4ade80, #16a34a);
      box-shadow: 0 0 20px 3px rgba(34,197,94,.55);
    }
    .toast-icon-badge.error {
      background: radial-gradient(circle at 35% 30%, #f87171, #dc2626);
      box-shadow: 0 0 20px 3px rgba(239,68,68,.55);
    }
    .toast-icon-badge.warning {
      background: radial-gradient(circle at 35% 30%, #fbbf24, #d97706);
      box-shadow: 0 0 20px 3px rgba(245,158,11,.55);
    }
    .toast-icon-badge.info {
      background: radial-gradient(circle at 35% 30%, #60a5fa, #2563eb);
      box-shadow: 0 0 20px 3px rgba(59,130,246,.55);
    }

    /* ── Body ───────────────────────────────────────── */
    .toast-body {
      flex: 1;
      min-width: 0;
      padding-top: 4px;
    }

    .toast-message {
      font-size: 1.05rem;
      font-weight: 700;
      line-height: 1.3;
      color: #ffffff;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
      word-break: break-word;
    }

    .toast-subtext {
      margin-top: 6px;
      font-size: 0.95rem;
      font-weight: 400;
      line-height: 1.4;
      color: #cbd5e1;
      word-break: break-word;
    }

    .toast-actions {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 22px;
    }

    .toast-btn {
      border: none;
      border-radius: 10px;
      padding: 10px 26px;
      font-size: 0.95rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      cursor: pointer;
      transition: transform 0.15s ease, opacity 0.15s ease, background-color 0.15s ease;
      white-space: nowrap;
    }

    .toast-btn:hover { transform: translateY(-1px); }

    .toast-btn-ghost {
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.28);
      color: #f1f5f9;
    }

    .toast-btn-ghost:hover {
      background: rgba(255, 255, 255, 0.14);
    }

    /* Solid accent button — color follows the toast type, like the
       orange "YES" button in the reference design. */
    .toast-btn-solid {
      color: #1c1300;
    }
    .toast-btn-solid:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }

    .toast-success .toast-btn-solid { background: linear-gradient(135deg, #4ade80, #16a34a); color: #052e16; }
    .toast-error   .toast-btn-solid { background: linear-gradient(135deg, #f87171, #dc2626); color: #450a0a; }
    .toast-warning .toast-btn-solid { background: linear-gradient(135deg, #fbbf24, #f59e0b); color: #1c1300; }
    .toast-info    .toast-btn-solid { background: linear-gradient(135deg, #60a5fa, #2563eb); color: #0c1e3d; }

    /* ── Close (×) ──────────────────────────────────── */
    .toast-close-btn {
      position: absolute;
      top: 16px;
      right: 12px;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.22);
      border-radius: 9px;
      color: #cbd5e1;
      font-size: 0.95rem;
      line-height: 1;
      cursor: pointer;
      padding: 0;
      transition: color 0.15s ease, background-color 0.15s ease;
    }

    .toast-close-btn:hover {
      color: #f8fafc;
      background: rgba(255, 255, 255, 0.12);
    }

    /* ── Responsive: shrink everything for small screens ────── */
    @media (max-width: 480px) {
      .alert-overlay {
        top: 12px;
        right: 12px;
        left: 12px;
        min-width: 0;
        max-width: none;
        gap: 10px;
      }

      .toast-card {
        gap: 14px;
        padding: 16px 44px 16px 16px;
        border-radius: 14px;
      }

      .toast-icon-badge {
        width: 44px;
        height: 44px;
        font-size: 1.15rem;
      }

      .toast-message {
        font-size: 1rem;
      }

      .toast-subtext {
        font-size: 0.82rem;
        margin-top: 4px;
      }

      .toast-actions {
        gap: 8px;
        margin-top: 16px;
      }

      .toast-btn {
        padding: 8px 16px;
        font-size: 0.82rem;
        border-radius: 8px;
      }

      .toast-close-btn {
        top: 10px;
        right: 10px;
        width: 28px;
        height: 28px;
        font-size: 0.8rem;
        border-radius: 7px;
      }
    }

    /* ── Review-prompt modal(s) — unchanged ─────────── */
    .rp-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9998; }
    .rp-modal {
      position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      z-index: 9999; width: min(90vw, 380px); background: #fff; border-radius: 12px;
      padding: 10px 20px 20px 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); animation: popIn 0.2s ease-out;
      display:flex;   flex-direction:column;
    }
    
    @keyframes popIn { from { transform: translate(-50%, -50%) scale(0.95); opacity: 0; } to { transform: translate(-50%, -50%) scale(1); opacity: 1; } }

    .rp-close-btn {
      background: none; border: none; font-size: 1.1rem; color: #94a3b8; line-height: 1;
      cursor: pointer; padding: 4px;
      margin-bottom:5px;
    }
    .rp-close-btn:hover { color: #475569; }

    .rp-apology {
      font-size: 0.82rem; color: #92400e; background: #fffbeb;
      border: 1px solid #fde68a; border-radius: 8px; padding: 8px 10px;
      margin-bottom: 12px;
    }

    .rp-row { margin-bottom: 14px; }
    .rp-copy-row { display: flex; align-items: center; gap: 8px; }
    .rp-review-text { flex: 1; font-size: 0.85rem; color: #374151; max-height: 60px; overflow-y: auto; white-space: pre-wrap; }
    .rp-question { font-weight: 500; text-align: center; color: #1f2937; }
    .rp-buttons { display: flex; justify-content: center; gap: 12px; margin-bottom: 0; }

    .rp-copy-btn { position: relative; overflow: hidden; transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease; }
    .rp-copy-btn.copied { background-color: #198754 !important; border-color: #198754 !important; color: #fff !important; }
    .rp-copy-icon-wrap { display: inline-block; transition: transform 0.25s ease; }
    .rp-copy-btn.copied .rp-copy-icon-wrap { animation: popCheck 0.3s ease-out; }
    @keyframes popCheck { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); } }

    @media (max-width: 480px) {
      .rp-modal {
        width: min(94vw, 340px);
        padding: 16px;
        border-radius: 10px;
      }
      .rp-review-text { font-size: 0.78rem; max-height: 50px; }
      .rp-question { font-size: 0.9rem; }
      .rp-apology { font-size: 0.76rem; padding: 6px 8px; }
    }
  `],
  template: `
    <!-- Standard toast alerts -->
    <div class="alert-overlay">
      @for (alert of toastAlerts(); track alert.id) {
        <div class="toast-card"
            [ngClass]="{
              'toast-success': alert.type === 'success',
              'toast-error': alert.type === 'error',
              'toast-warning': alert.type === 'warning',
              'toast-info': alert.type === 'info' || !alert.type
            }">

          <div class="toast-icon-badge"
              [ngClass]="{
                'success': alert.type === 'success',
                'error': alert.type === 'error',
                'warning': alert.type === 'warning',
                'info': alert.type === 'info' || !alert.type
              }">
            @switch (alert.type) {
              @case ('success') { <i class="bi bi-check-lg"></i> }
              @case ('error') { <i class="bi bi-exclamation-triangle-fill"></i> }
              @case ('warning') { <i class="bi bi-exclamation-triangle-fill"></i> }
              @default { <i class="bi bi-info-lg"></i> }
            }
          </div>

          <div class="toast-body">
            <div class="toast-message">{{ alert.message }}</div>

            @if (alert.actionText || alert.cancelText) {
              <div class="toast-actions">

                @if (alert.cancelText) {
                  <button
                    type="button"
                    class="toast-btn toast-btn-ghost"
                    (click)="alertService.triggerCancel(alert)">
                    {{ alert.cancelText }}
                  </button>
                }

                @if (alert.actionText) {
                  <button
                    type="button"
                    class="toast-btn toast-btn-solid"
                    (click)="alertService.triggerAction(alert)">
                    {{ alert.actionText }}
                  </button>
                }

              </div>
            }
          </div>

          <button type="button" class="toast-close-btn" (click)="alertService.remove(alert.id)" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      }
    </div>

    <!-- Review-prompt modal(s) -->
    @for (alert of reviewPromptAlerts(); track alert.id) {
      <div class="rp-backdrop" (click)="handleDismiss(alert)"></div>
      <div class="rp-modal ">
        <div class=" w-100 d-flex justify-content-end">
          <!-- Close (×) button — distinct from "No" -->
          <button type="button" class="rp-close-btn" (click)="handleDismiss(alert)" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        
        <div>
          <!-- Low-rating apology, shown above the copy row -->
          @if (alert.ratingAtPrompt && alert.ratingAtPrompt < 3) {
            <div class="rp-apology">
              We're sorry your experience wasn't up to the mark. Your feedback has been noted, and we'll work on doing better.
            </div>
          }

          <!-- Top row: copy to clipboard -->
          <div class="rp-row rp-copy-row">
            <span class="rp-review-text">{{ alert.reviewText }}</span>
            <button
              class="btn btn-sm btn-outline-secondary rp-copy-btn"
              [class.copied]="copiedAlertId() === alert.id"
              (click)="copyToClipboard(alert)">
              <span class="rp-copy-icon-wrap">
                <fa-icon [icon]="copiedAlertId() === alert.id ? icons.checkCircle : icons.copy"></fa-icon>
              </span>
              {{ copiedAlertId() === alert.id ? 'Copied' : '' }}
            </button>
          </div>

          <!-- Middle row: ask about Google review -->
          <div class="rp-row rp-question">
            Would you like to rate {{ alert.vendorDisplayName || 'us' }} on Google Reviews?
          </div>

          <!-- Bottom row: Yes/No -->
          <div class="rp-row rp-buttons">
            <button class="btn btn-outline-secondary" (click)="handleNo(alert)">No</button>
            <button class="btn btn-dark" (click)="handleYes(alert)">Yes</button>
          </div>
        </div>
      </div>
    }
  `
})
export class AlertComponent {
  alertService = inject(AlertService);
  private cusReviewService = inject(CusReviewService);
  icons = faIcons;

  copiedAlertId = signal<number | null>(null);
  private copyResetTimer?: ReturnType<typeof setTimeout>;

  toastAlerts() { return this.alertService.alerts().filter(a => !a.isReviewPrompt); }
  reviewPromptAlerts() { return this.alertService.alerts().filter(a => a.isReviewPrompt); }

  copyToClipboard(alert: Alert): void {
    if (!alert.reviewText) return;
    navigator.clipboard.writeText(alert.reviewText).then(() => {
      this.copiedAlertId.set(alert.id);
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => this.copiedAlertId.set(null), 1500);
    }).catch(() => {});
  }

  handleYes(alert: Alert): void {
    this.trackAction(alert, 'clicked_yes');
    alert.onYes?.();
    this.alertService.remove(alert.id);
  }

  handleNo(alert: Alert): void {
    this.trackAction(alert, 'clicked_no');
    alert.onNo?.();
    this.alertService.remove(alert.id);
  }

  handleDismiss(alert: Alert): void {
    this.trackAction(alert, 'dismissed');
    alert.onNo?.();
    this.alertService.remove(alert.id);
  }

  private trackAction(alert: Alert, action: 'clicked_yes' | 'clicked_no' | 'dismissed'): void {
    if (!alert.purchaseId || !alert.reviewId) return;

    this.cusReviewService.trackGoogleReviewAction({
      purchaseId: alert.purchaseId,
      reviewId: alert.reviewId,
      source: 'post_review',
      action,
      ratingAtPrompt: alert.ratingAtPrompt ?? 0
    }).subscribe();
  }
}